/* eslint-disable jsx-a11y/anchor-has-content */
import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'components/UI/button/button'
import { MaskedInput } from 'components/UI/input/masked-input'
import Modal from 'components/UI/modal/modal'
import SubBody from 'components/UI/sub-body/sub-body'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAuthMutation, useSendOtpMutation } from 'redux/api/auth.api'
import { isAuthSelector } from 'redux/store/slices/auth-slice'
import tw from 'twin.macro'
import { pathnames } from 'utils/steps'

import { createPhoneSchema } from '../../share/schemas/phone.schema'
import OtpVerify from './otp-verify'

function AuthModal({
  open,
  onClose,
  denomination,
  channel,
  cartomatPhoneNumber
}: {
  open: boolean
  onClose: () => void
  denomination?: { amount: string; product_code: string }
  channel: 'auth'
  cartomatPhoneNumber?: string
}) {
  const [openOtp, setOpenOtp] = useState<boolean>(false)
  const [activeTitle, setActiveTitle] = useState<string | null>()
  const isAuth = useSelector(isAuthSelector)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const phoneSchema = useMemo(() => createPhoneSchema(t), [i18n.language])
  const {
    control,
    formState: { errors, isValid },
    getValues,
    setValue,
    setError,
    watch,
    reset
  } = useForm({
    defaultValues: { mobile_phone: '', code: '' },
    resolver: yupResolver(phoneSchema),
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    if (cartomatPhoneNumber) {
      setValue('mobile_phone', cartomatPhoneNumber, { shouldValidate: true })
    }
  }, [cartomatPhoneNumber, setValue])

  const [auth, { isSuccess: authSuccess, error: authError, isLoading: authLoading }] = useAuthMutation()
  const [sendOtp, { isSuccess: sendOtpSuccess, isLoading: sendOtpLoading }] = useSendOtpMutation()

  const handleSendOtp = async () => {
    try {
      const mobile_phone = getValues().mobile_phone.replace(/[^\d+]/g, '')
      if (!mobile_phone.includes('+')) {
        await sendOtp({ mobile_phone: '+' + mobile_phone }).unwrap()
      } else {
        await sendOtp({ mobile_phone }).unwrap()
      }
    } catch (error) {
      const typedErr = error as {
        data: {
          error: { description: string | { mobile_phone: string[] }; code: string }
        }
        status: number
      }
      if (typedErr.data.error.code === 'INVALID_OTP' && typeof typedErr.data.error.description === 'string') {
        setOpenOtp(true)
        setError('code', { type: 'validate', message: typedErr.data.error.description })
      } else if (typedErr.data.error.code === 'NO_ACTIVE_CARD') {
        setError('mobile_phone', { type: 'validate', message: t('error_no_active_card', { ns: 'auth' }) })
      } else {
        if (typeof typedErr.data.error.description === 'string') {
          setError('mobile_phone', { type: 'validate', message: typedErr.data.error.description })
        } else if (typedErr.data.error.description?.mobile_phone) {
          setError('mobile_phone', {
            type: 'validate',
            message: typedErr.data.error.description.mobile_phone[0]
          })
        }
      }
    }
  }

  const handleOtpVerify = async () => {
    try {
      await auth({ mobile_phone: '+' + watch('mobile_phone').replace(/[^\d+]/g, ''), code: watch('code') }).unwrap()
    } catch (error) {
      const typedErr = error as {
        data: {
          error: { description: string }
        }
        status: number
      }
      if (typedErr.data.error.description) {
        setError('code', { type: 'validate', message: typedErr.data.error.description })
      }
    }
  }
  useEffect(() => {
    if (authSuccess) {
      setActiveTitle('')
    } else if (openOtp && !authSuccess) {
      setActiveTitle(t('modal_title', { ns: 'otp' }))
    } else {
      setActiveTitle(t('title', { ns: 'auth' }))
    }
  }, [sendOtpSuccess, authSuccess, openOtp, t])

  useEffect(() => {
    if (open && isAuth) {
      navigate(pathnames['personal-cabinet'])
      reset()
      onClose?.()
    }
  }, [isAuth])

  useEffect(() => {
    sendOtpSuccess && setOpenOtp(true)
  }, [sendOtpSuccess])

  useEffect(() => {
    if (!open) {
      setOpenOtp(false)
      reset()
    }
  }, [open])

  useEffect
  return (
    <Modal
      open={open}
      withClose={!authSuccess}
      onClose={onClose}
      withBackdrop={false}
      outsideClose={false}
      title={activeTitle}
    >
      <div tw='sm:w-[422px] w-full'>
        {openOtp ? (
          <OtpVerify
            phone={watch('mobile_phone')}
            control={control}
            name='code'
            isOtpValid={isValid}
            isLoading={authLoading}
            onOtpVerify={handleOtpVerify}
          />
        ) : (
          <div tw='flex flex-col gap-5 mt-5'>
            <MaskedInput
              control={control}
              name={'mobile_phone'}
              label={t('mobile_phone', { ns: 'auth' })}
              mask={'+7(999)999 99 99'}
              placeholder={'+7 ( _ _ _ ) _ _ _  _ _  _ _'}
              error={errors.mobile_phone?.message}
            />
            <Button variant='gradient' loading={sendOtpLoading} disabled={!isValid} onClick={handleSendOtp}>
              {t('receive_sms', { ns: 'auth' })}
            </Button>
            <SubBody twStyle={tw`text-secondary`}>
              <Trans
                components={{
                  link1: (
                    <a
                      onClick={() =>
                        window.open(
                          'https://bankffin.kz/storage/docs/DW6oJlZFR8SQOYyRgJuIdKMYLXAj4M7qbhtMzdma.pdf',
                          '_self'
                        )
                      }
                      href='https://bankffin.kz/storage/docs/DW6oJlZFR8SQOYyRgJuIdKMYLXAj4M7qbhtMzdma.pdf'
                      tw='underline font-medium '
                    />
                  )
                }}
              >
                {t('agreement', {
                  ns: 'auth'
                })}
              </Trans>
            </SubBody>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default AuthModal
